import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { adminClient, OfflineRequest } from '@/api/adminClient'
import { Play, Trash2, ChevronDown, ChevronUp, RefreshCw, Clock, AlertCircle, CheckCircle } from 'lucide-react'

export default function OfflineQueueTab() {
  const [requests, setRequests] = useState<OfflineRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [expandedRequestId, setExpandedRequestId] = useState<number | null>(null)
  
  // Stabilní toast funkce
  const toast = useCallback(({ title, description }: { title: string; description?: string; variant?: string }) => {
    alert(`${title}${description ? '\n' + description : ''}`)
  }, [])

  const loadRequests = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await adminClient.getOfflineQueue()
      setRequests(data)
    } catch (error) {
      console.error('Chyba při načítání offline fronty:', error)
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst offline frontu',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadRequests()
  }, [loadRequests])

  const processRequest = async (id: number) => {
    try {
      setIsProcessing(true)
      await adminClient.processOfflineRequest(id)
      
      toast({
        title: 'Úspěch',
        description: 'Request byl úspěšně zpracován',
      })
      
      await loadRequests()
    } catch (error) {
      console.error('Chyba při zpracování requestu:', error)
      toast({
        title: 'Chyba',
        description: error instanceof Error ? error.message : 'Chyba při zpracování requestu',
        variant: 'destructive'
      })
      await loadRequests() // Aktualizovat i při chybě pro zobrazení nového stavu
    } finally {
      setIsProcessing(false)
    }
  }

  const processAllRequests = async () => {
    try {
      setIsProcessing(true)
      const result = await adminClient.processAllOfflineRequests()
      
      toast({
        title: result.success ? 'Úspěch' : 'Dokončeno s chybami',
        description: result.message,
        variant: result.success ? 'default' : 'destructive'
      })
      
      if (result.errors && result.errors.length > 0) {
        console.log('Chyby při hromadném zpracování:', result.errors)
      }
      
      await loadRequests()
    } catch (error) {
      console.error('Chyba při hromadném zpracování:', error)
      toast({
        title: 'Chyba',
        description: error instanceof Error ? error.message : 'Chyba při hromadném zpracování',
        variant: 'destructive'
      })
      await loadRequests()
    } finally {
      setIsProcessing(false)
    }
  }

  const deleteRequest = async (id: number) => {
    if (!confirm('Opravdu chcete smazat tento request?')) {
      return
    }

    try {
      await adminClient.deleteOfflineRequest(id)
      
      toast({
        title: 'Úspěch',
        description: 'Request byl smazán',
      })
      
      await loadRequests()
    } catch (error) {
      console.error('Chyba při mazání requestu:', error)
      toast({
        title: 'Chyba',
        description: 'Chyba při mazání requestu',
        variant: 'destructive'
      })
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Čeká
        </Badge>
      case 'sent':
        return <Badge variant="default" className="flex items-center gap-1 bg-green-600">
          <CheckCircle className="h-3 w-3" />
          Odesláno
        </Badge>
      case 'failed':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Chyba
        </Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ')
  }

  const formatPayload = (payload: Record<string, unknown>) => {
    return JSON.stringify(payload, null, 2)
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length
  const sentCount = requests.filter(r => r.status === 'sent').length
  const failedCount = requests.filter(r => r.status === 'failed').length

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          Načítám offline frontu...
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Statistiky a akce */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Celkem</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{requests.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Čeká</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingCount}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Odesláno</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{sentCount}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Chyby</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{failedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Akční tlačítka */}
      <Card>
        <CardHeader>
          <CardTitle>Akce</CardTitle>
          <CardDescription>
            Zpracování čekajících requestů a správa fronty
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button 
            onClick={loadRequests} 
            variant="outline"
            disabled={isProcessing}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Obnovit
          </Button>
          
          {pendingCount > 0 && (
            <Button 
              onClick={processAllRequests}
              disabled={isProcessing}
            >
              <Play className="h-4 w-4 mr-2" />
              {isProcessing ? 'Zpracovávám...' : `Zpracovat všechny (${pendingCount})`}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Seznam requestů */}
      <Card>
        <CardHeader>
          <CardTitle>Offline fronta ({requests.length})</CardTitle>
          <CardDescription>
            Přehled všech requestů v offline frontě
          </CardDescription>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Žádné requesty v offline frontě</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div key={request.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-medium">
                          {request.request_type} #{request.id}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(request.created_at)}
                          {request.retry_count > 0 && (
                            <span className="ml-2">
                              • {request.retry_count} pokusů
                            </span>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {request.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => processRequest(request.id)}
                          disabled={isProcessing}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Zpracovat
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedRequestId(
                          expandedRequestId === request.id ? null : request.id
                        )}
                      >
                        {expandedRequestId === request.id ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteRequest(request.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {request.error_message && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      <strong>Chyba:</strong> {request.error_message}
                    </div>
                  )}
                  
                  {request.last_attempt && (
                    <div className="mt-1 text-sm text-gray-500">
                      Poslední pokus: {formatDate(request.last_attempt)}
                    </div>
                  )}
                  
                  {expandedRequestId === request.id && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-2">Payload:</h4>
                      <pre className="text-sm bg-white p-3 rounded border overflow-auto max-h-96">
                        {formatPayload(request.payload)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
} 